import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, ArrowLeft, Camera, CheckCircle2, Phone, RefreshCw, Eraser } from "lucide-react";
import { toast } from "sonner";

export default function MobileDeliveryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [pos, setPos] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/mobile/deliveries/${id}`);
      setDoc(data);
    } catch (e) {
      toast.error("Delivery not found");
      nav("/m/deliveries");
    }
  };
  useEffect(() => { load(); getLocation(); }, []);

  const getLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Photo too large (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  // Signature canvas
  const canvasCoords = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return [x * (c.width / r.width), y * (c.height / r.height)];
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const [x, y] = canvasCoords(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const [x, y] = canvasCoords(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#2C1810";
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setSignature(canvasRef.current.toDataURL("image/png"));
  };
  const clearSig = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setSignature(null);
  };

  const submit = async () => {
    if (!signature) {
      toast.error("Signature required to confirm delivery");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/mobile/deliveries/${id}/deliver`, {
        signature_base64: signature,
        photo_base64: photo,
        lat: pos?.lat, lng: pos?.lng,
        notes,
      });
      toast.success("Delivery marked complete");
      nav("/m/deliveries");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!doc) return <div className="p-6 text-[#8B7355] text-sm">Loading…</div>;

  const isDelivered = doc.delivery_status === "delivered";

  return (
    <div className="pb-4" data-testid="m-delivery-detail">
      <div className="p-4">
        <Link to="/m/deliveries" className="flex items-center gap-1 text-sm text-[#8B7355] mb-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="text-[10px] uppercase tracking-widest text-[#C05621]">{doc.doc_type}</div>
        <h1 className="font-[Outfit] text-2xl font-semibold tracking-tight">{doc.number}</h1>
      </div>

      {/* Customer card */}
      <div className="mx-4 bg-white border border-[#EAE5D9] rounded-xl p-4 mb-4">
        <div className="text-[11px] uppercase tracking-widest text-[#8B7355] mb-1">Deliver to</div>
        <div className="font-semibold">{doc.customer_snapshot?.name}</div>
        {doc.customer_snapshot?.company && <div className="text-sm text-[#5C4033]">{doc.customer_snapshot.company}</div>}
        <div className="flex items-center gap-4 mt-3 text-sm">
          {doc.customer_snapshot?.phone && (
            <a href={`tel:${doc.customer_snapshot.phone}`} className="flex items-center gap-1 text-[#C05621]" data-testid="m-call-btn">
              <Phone className="w-4 h-4" /> Call
            </a>
          )}
          {doc.customer_snapshot?.city && (
            <a
              href={`https://www.google.com/maps/search/${encodeURIComponent((doc.customer_snapshot?.company || doc.customer_snapshot?.name) + " " + (doc.customer_snapshot?.city || ""))}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-[#C05621]"
              data-testid="m-navigate-btn"
            >
              <MapPin className="w-4 h-4" /> Navigate ({doc.customer_snapshot.city})
            </a>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="mx-4 bg-white border border-[#EAE5D9] rounded-xl p-4 mb-4">
        <div className="text-[11px] uppercase tracking-widest text-[#8B7355] mb-2">Items</div>
        <ul className="divide-y divide-[#EAE5D9]">
          {(doc.items || []).map((it, i) => (
            <li key={i} className="flex items-start justify-between py-2 text-sm">
              <div>
                <div className="font-medium">{it.name}</div>
                <div className="text-xs text-[#8B7355]">{it.quantity} {it.unit}</div>
              </div>
              <div className="text-right">₹{Math.round((it.quantity || 0) * (it.unit_price || 0)).toLocaleString("en-IN")}</div>
            </li>
          ))}
        </ul>
        <div className="flex justify-between mt-3 pt-3 border-t border-[#EAE5D9] font-semibold">
          <span>Total</span>
          <span>₹{Math.round(doc.grand_total || 0).toLocaleString("en-IN")}</span>
        </div>
      </div>

      {isDelivered ? (
        <div className="mx-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          <div>
            <div className="font-semibold text-emerald-800">Delivered</div>
            <div className="text-xs text-emerald-700">Marked at {new Date(doc.delivered_at).toLocaleString("en-IN")}</div>
          </div>
        </div>
      ) : (
        <>
          {/* Photo */}
          <div className="mx-4 bg-white border border-[#EAE5D9] rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-[#8B7355]">Proof photo (optional)</div>
              {photo && <button onClick={() => setPhoto(null)} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
            {photo ? (
              <img src={photo} alt="Proof" className="w-full h-48 object-cover rounded-md border border-[#EAE5D9]" data-testid="m-photo-preview" />
            ) : (
              <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-[#EAE5D9] rounded-md text-[#8B7355] text-sm cursor-pointer hover:border-[#C05621]" data-testid="m-photo-drop">
                <Camera className="w-6 h-6 mb-1" />
                Take photo
                <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="m-photo-input" />
              </label>
            )}
          </div>

          {/* Signature */}
          <div className="mx-4 bg-white border border-[#EAE5D9] rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-[#8B7355]">Customer signature *</div>
              <button onClick={clearSig} className="text-xs text-[#C05621] hover:underline flex items-center gap-1" data-testid="m-sig-clear">
                <Eraser className="w-3 h-3" /> Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={600}
              height={180}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              className="w-full h-40 bg-[#FDFBF7] border border-[#EAE5D9] rounded-md touch-none"
              data-testid="m-sig-canvas"
            />
            <div className="text-[10px] text-[#8B7355] mt-1 text-center">Ask the customer to sign above</div>
          </div>

          {/* Notes */}
          <div className="mx-4 mb-4">
            <div className="text-[11px] uppercase tracking-widest text-[#8B7355] mb-1">Notes (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Left at reception"
              className="w-full rounded-md border border-[#EAE5D9] px-3 py-2 text-sm outline-none focus:border-[#C05621] resize-none"
              data-testid="m-delivery-notes"
            />
          </div>

          {/* GPS */}
          <div className="mx-4 mb-4 bg-white border border-[#EAE5D9] rounded-md p-3 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1 text-[#5C4033]">
              <MapPin className="w-3 h-3" />
              {pos ? `${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}` : "Fetching…"}
            </div>
            <button onClick={getLocation} className="text-[#C05621] flex items-center gap-1" data-testid="m-refresh-gps">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          <div className="mx-4">
            <button
              onClick={submit}
              disabled={busy}
              data-testid="m-deliver-submit"
              className="w-full h-14 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold uppercase tracking-widest text-sm disabled:opacity-50"
            >
              {busy ? "Submitting…" : "✓ Confirm delivery"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
